import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type PointerEvent,
  type ReactElement,
} from "react";
import {
  applyCanvasMutation,
  type CanvasMutationHandler,
} from "./mutations.js";
import { Trash2 } from "lucide-react";
import { nanoid } from "nanoid";
import * as Y from "yjs";
import { Badge } from "../atoms/index.js";
import { StickyNoteCard, Tooltip } from "../molecules/index.js";
import { getContrastingTextColor } from "../lib/contrast-foreground.js";
import { SELECTED } from "../lib/surfaces.js";
import { cn } from "../lib/utils.js";
import { useCollectionFocusRecovery } from "../hooks/useCollectionFocusRecovery.js";
import { useCanvasViewport } from "./hooks/useCanvasViewport.js";

/**
 * Yjs-backed sticky-note layer for the DS `<Canvas>` — data-blind and
 * module-agnostic (graduated from `solve-feature` in PB-222 so a second module,
 * Hoshin, can reuse it without a sibling import; ADR-0009). Notes live inline in
 * the canvas Y.Doc under `stickyNotes`; rendered in the canvas
 * `viewportOverlaySlot` so they pan/zoom with the viewport.
 *
 * The "paper" shell is the shared `StickyNoteCard` molecule (PB-225); this layer
 * only supplies the editable body + drag/delete + the "draft" badge/author.
 */

// Shared default note size (the SOLVE canvas note). Callers wanting a different
// size pass `w`/`h` to `addStickyNote` — the Hoshin workshop uses a larger note.
const NOTE_W = 220;
const NOTE_H = 160;
// New notes tile left-to-right in rows (side by side, no overlap) instead of a
// diagonal stack, so a board reads as a grid.
const GRID_COLS = 2;
const GRID_GAP = 24;
const GRID_X = 240;
const GRID_Y = 200;

export function stickyNotesMap(doc: Y.Doc): Y.Map<Y.Map<unknown>> {
  return doc.getMap<Y.Map<unknown>>("stickyNotes");
}

/** Optional author shown as a small avatar on a note (the "draft" variant). */
export interface StickyNoteAuthor {
  readonly initials: string;
  /** Full name for the avatar's accessible label (falls back to initials). */
  readonly name?: string;
  /** Any CSS colour (awareness colour / token var) — applied as inline bg. */
  readonly color: string;
}

export function addStickyNote(
  doc: Y.Doc,
  position?: { readonly x: number; readonly y: number } | undefined,
  opts?:
    | {
        readonly text?: string;
        readonly w?: number;
        readonly h?: number;
        readonly surface?: string;
        readonly workshopId?: string;
        readonly notesMap?: Y.Map<Y.Map<unknown>>;
        readonly noteMetaMap?: Y.Map<number>;
        readonly maxTextLength?: number;
      }
    | undefined,
): string {
  const notes = opts?.notesMap ?? stickyNotesMap(doc);
  // Monotonic slot index (persisted in the doc) for the default grid placement.
  // Using `notes.size` would collide after a deletion — the map shrinks and a
  // later add reuses a still-occupied (col,row); this counter only increases.
  const meta = opts?.noteMetaMap ?? doc.getMap<number>("stickyNoteMeta");
  const prevSlot = meta.get("nextSlot");
  const slot = typeof prevSlot === "number" ? prevSlot : 0;
  const col = slot % GRID_COLS;
  const row = Math.floor(slot / GRID_COLS);
  const now = Date.now();
  const id = nanoid();
  // Size defaults to the shared note; a caller (e.g. the Hoshin workshop) may
  // override w/h without changing the size for other consumers (SOLVE).
  const w = opts?.w ?? NOTE_W;
  const h = opts?.h ?? NOTE_H;
  const note = new Y.Map<unknown>();

  // Generic canvas notes start empty and rely on the textarea placeholder ("Add
  // note"), so a new note never carries literal filler the user must delete; the
  // draft/brainstorm caller likewise passes "" for its own placeholder.
  note.set(
    "text",
    opts?.maxTextLength === undefined
      ? (opts?.text ?? "")
      : (opts?.text ?? "").slice(0, opts.maxTextLength),
  );
  note.set("x", position?.x ?? GRID_X + col * (w + GRID_GAP));
  note.set("y", position?.y ?? GRID_Y + row * (h + GRID_GAP));
  note.set("w", w);
  note.set("h", h);
  note.set("z", now);
  note.set("createdAt", now);
  if (opts?.surface !== undefined) note.set("surface", opts.surface);
  if (opts?.workshopId !== undefined) note.set("workshopId", opts.workshopId);

  doc.transact(() => {
    notes.set(id, note);
    meta.set("nextSlot", slot + 1);
  });

  return id;
}

export function StickyNoteLayer({
  doc,
  canvasW,
  canvasH,
  canvasMinX = 0,
  canvasMinY = 0,
  readOnly,
  resolveNoteVisibility,
  resolveNoteLabel,
  variant = "note",
  author,
  selectedNoteId,
  focusNoteId,
  onNoteSelect,
  notesMap,
  maxTextLength,
  onCanvasMutation,
  focusAfterLastDelete,
}: {
  readonly doc: Y.Doc;
  readonly canvasW: number;
  readonly canvasH: number;
  /**
   * Lowest coordinate a note may be dragged to. Default 0.
   *
   * Raise the reachable region negative on a canvas whose pan extent has slack on the
   * top/left, or notes cannot be dragged left or up past the origin at all.
   */
  readonly canvasMinX?: number | undefined;
  readonly canvasMinY?: number | undefined;
  readonly readOnly?: boolean | undefined;
  /** Optional host visibility resolver. Hidden notes remain stored in Yjs. */
  readonly resolveNoteVisibility?:
    | ((note: Y.Map<unknown>) => boolean)
    | undefined;
  /** Optional host-owned note label; omitted notes keep the generic "Note". */
  readonly resolveNoteLabel?:
    | ((note: Y.Map<unknown>) => string | undefined)
    | undefined;
  /** "draft" shows a DRAFT badge + author avatar (workshop brainstorm, PB-223).
   *  Default "note" is the generic canvas note (grip + delete). */
  readonly variant?: "note" | "draft" | undefined;
  /** Author avatar for the "draft" variant (all notes; single-user MVP). */
  readonly author?: StickyNoteAuthor | undefined;
  /** Optional host-owned selection. Omitted callers keep existing behaviour. */
  readonly selectedNoteId?: string | undefined;
  /** Focus the matching note's editor after an external create action. Kept
   *  separate from selection so selecting a drag handle never steals focus. */
  readonly focusNoteId?: string | undefined;
  readonly onNoteSelect?: ((noteId: string) => void) | undefined;
  /** Optional host-owned note container; defaults to the generic sticky-note map. */
  readonly notesMap?: Y.Map<Y.Map<unknown>> | undefined;
  /** Optional host-owned text cap. Omitted consumers keep the shared default. */
  readonly maxTextLength?: number | undefined;
  readonly onCanvasMutation?: CanvasMutationHandler | undefined;
  readonly focusAfterLastDelete?: (() => HTMLElement | null) | undefined;
}): ReactElement {
  const notes = notesMap ?? stickyNotesMap(doc);
  const { x, y, zoom } = useCanvasViewport();
  const [, force] = useState(0);

  useEffect(() => {
    const observe = (): void => force((n) => n + 1);
    notes.observeDeep(observe);
    return (): void => notes.unobserveDeep(observe);
  }, [notes]);

  const entries: Array<[string, Y.Map<unknown>]> = [];
  notes.forEach((note, id) => {
    if (resolveNoteVisibility?.(note) === false) return;
    entries.push([id, note]);
  });
  entries.sort(([, a], [, b]) => noteNumber(a, "z", 0) - noteNumber(b, "z", 0));
  const focusRecovery = useCollectionFocusRecovery(
    entries.map(([id]) => id),
    focusAfterLastDelete ?? (() => null),
  );

  return (
    <div
      data-testid="sticky-note-layer"
      {...focusRecovery}
      style={{
        position: "absolute",
        inset: 0,
        transformOrigin: "0 0",
        transform: `translate(${x}px, ${y}px) scale(${zoom})`,
        pointerEvents: "none",
      }}
    >
      {entries.map(([id, note]) => (
        <StickyNote
          key={id}
          id={id}
          doc={doc}
          notes={notes}
          note={note}
          label={resolveNoteLabel?.(note)}
          canvasW={canvasW}
          canvasMinX={canvasMinX}
          canvasMinY={canvasMinY}
          canvasH={canvasH}
          zoom={zoom}
          readOnly={readOnly}
          variant={variant}
          author={author}
          selected={selectedNoteId === id}
          focusEditor={focusNoteId === id}
          onSelect={onNoteSelect}
          maxTextLength={maxTextLength}
          onCanvasMutation={onCanvasMutation}
        />
      ))}
    </div>
  );
}

function StickyNote({
  id,
  doc,
  notes,
  note,
  label,
  canvasW,
  canvasH,
  canvasMinX,
  canvasMinY,
  zoom,
  readOnly,
  variant,
  author,
  selected,
  focusEditor,
  onSelect,
  maxTextLength,
  onCanvasMutation,
}: {
  readonly id: string;
  readonly doc: Y.Doc;
  readonly notes: Y.Map<Y.Map<unknown>>;
  readonly note: Y.Map<unknown>;
  readonly label?: string | undefined;
  readonly canvasW: number;
  readonly canvasH: number;
  readonly canvasMinX: number;
  readonly canvasMinY: number;
  readonly zoom: number;
  readonly readOnly?: boolean | undefined;
  readonly variant?: "note" | "draft" | undefined;
  readonly author?: StickyNoteAuthor | undefined;
  readonly selected: boolean;
  readonly focusEditor: boolean;
  readonly onSelect?: ((noteId: string) => void) | undefined;
  readonly maxTextLength?: number | undefined;
  readonly onCanvasMutation?: CanvasMutationHandler | undefined;
}): ReactElement {
  const isDraft = variant === "draft";
  const [, force] = useState(0);
  // Teardown for an in-flight drag, so the window listeners don't leak if the
  // note unmounts mid-drag (deleted / Yjs re-key) before pointerup fires.
  const dragTeardown = useRef<(() => void) | null>(null);
  const editorRef = useRef<HTMLTextAreaElement>(null);
  const readOnlyRef = useRef(Boolean(readOnly));

  useEffect(() => {
    const observe = (): void => force((n) => n + 1);
    note.observe(observe);
    return (): void => note.unobserve(observe);
  }, [note]);

  useEffect(() => () => dragTeardown.current?.(), []);
  useEffect(() => {
    if (focusEditor && !readOnly) {
      editorRef.current?.focus({ preventScroll: true });
    }
  }, [focusEditor, readOnly]);
  useLayoutEffect(() => {
    readOnlyRef.current = Boolean(readOnly);
    if (readOnly) dragTeardown.current?.();
  }, [readOnly]);

  const x = noteNumber(note, "x", 0);
  const y = noteNumber(note, "y", 0);
  const w = noteNumber(note, "w", NOTE_W);
  const h = noteNumber(note, "h", NOTE_H);
  const text = noteString(note, "text", "");
  const authorForeground = author
    ? getContrastingTextColor(author.color)
    : undefined;

  const setPosition = (
    nextX: number,
    nextY: number,
    gestureId: string,
  ): void => {
    if (readOnlyRef.current) return;
    applyCanvasMutation(
      onCanvasMutation,
      { target: "note", action: "move", id, entry: note, gestureId },
      () =>
        doc.transact(() => {
          note.set("x", nextX);
          note.set("y", nextY);
          note.set("z", Date.now());
        }),
    );
  };

  const onPointerDown = (e: PointerEvent<HTMLDivElement>): void => {
    dragTeardown.current?.();
    if (e.button !== 0) return;
    onSelect?.(id);
    if (readOnlyRef.current) return;
    e.preventDefault();
    e.stopPropagation();
    const gestureId = crypto.randomUUID();
    let last = { x: e.clientX, y: e.clientY };
    let currentX = x;
    let currentY = y;
    const target = e.currentTarget;
    target.setPointerCapture(e.pointerId);
    const activePointerId = e.pointerId;
    const cleanup = (): void => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
      window.removeEventListener("pointercancel", up);
      dragTeardown.current = null;
      try {
        if (target.hasPointerCapture(activePointerId)) {
          target.releasePointerCapture(activePointerId);
        }
      } catch {
        /* pointer already released */
      }
    };
    const move = (ev: globalThis.PointerEvent): void => {
      if (ev.pointerId !== activePointerId) return;
      if (readOnlyRef.current) {
        cleanup();
        return;
      }
      const dx = ev.clientX - last.x;
      const dy = ev.clientY - last.y;
      last = { x: ev.clientX, y: ev.clientY };
      currentX = clamp(
        currentX + dx / zoom,
        canvasMinX,
        Math.max(canvasMinX, canvasW - w),
      );
      currentY = clamp(
        currentY + dy / zoom,
        canvasMinY,
        Math.max(canvasMinY, canvasH - h),
      );
      setPosition(currentX, currentY, gestureId);
    };
    const up = (ev: globalThis.PointerEvent): void => {
      if (ev.pointerId !== activePointerId) return;
      cleanup();
    };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
    window.addEventListener("pointercancel", up);
    dragTeardown.current = cleanup;
  };

  return (
    <StickyNoteCard
      data-testid="sticky-note"
      data-focus-key={id}
      label={label ?? "Note"}
      className={cn(
        "group/sticky transition-[box-shadow,border-color] duration-motion-state ease-motion-standard motion-reduce:transition-none",
        selected && SELECTED,
      )}
      style={{
        position: "absolute",
        left: x,
        top: y,
        width: w,
        height: h,
        pointerEvents: "auto",
      }}
      tabIndex={readOnly && onSelect ? 0 : undefined}
      onFocus={() => onSelect?.(id)}
      onHeaderPointerDown={onPointerDown}
      headerClassName={
        readOnly ? "cursor-default" : "cursor-grab active:cursor-grabbing"
      }
      trailingSlot={
        isDraft ? (
          <Badge variant="inherit" size="xs" shape="pill" uppercase>
            Draft
          </Badge>
        ) : readOnly ? null : (
          <Tooltip content="Delete sticky note">
            {/* eslint-disable-next-line ds/no-hand-built -- today's sticky-paper paint (65% rest opacity, sticky-line hover wash); Button plain on sticky paper is look row OK-06, which is not a T10 row, so the look stays */}
            <button
              type="button"
              // The header runs `onPointerDown` to start a drag (setPointerCapture),
              // which would otherwise swallow this button's click. Stop the
              // pointerdown here so the click — and the delete — actually fires.
              onPointerDown={(e) => e.stopPropagation()}
              onClick={(e) => {
                if (readOnlyRef.current) return;
                e.preventDefault();
                e.stopPropagation();
                applyCanvasMutation(
                  onCanvasMutation,
                  { target: "note", action: "delete", id, entry: note },
                  () => doc.transact(() => notes.delete(id)),
                );
              }}
              aria-label="Delete sticky note"
              className="rounded p-1 opacity-65 hover:bg-pb-sticky-line/40 hover:opacity-100"
            >
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          </Tooltip>
        )
      }
    >
      <textarea
        ref={editorRef}
        value={text}
        disabled={readOnly}
        maxLength={maxTextLength}
        onBlur={() =>
          onCanvasMutation?.(
            { target: "note", action: "finish", id, entry: note },
            () => {},
          )
        }
        onChange={(e) => {
          if (readOnlyRef.current) return;
          const next =
            maxTextLength === undefined
              ? e.target.value
              : e.target.value.slice(0, maxTextLength);
          applyCanvasMutation(
            onCanvasMutation,
            { target: "note", action: "edit", id, entry: note, field: "text" },
            () => doc.transact(() => note.set("text", next)),
          );
        }}
        className="min-h-0 flex-1 resize-none rounded-md bg-transparent p-3 text-body-small leading-5 [--ring:var(--pb-sticky-ink)] focus-field placeholder:text-pb-sticky-ink/45 disabled:cursor-default"
        placeholder={isDraft ? "Type a note…" : "Add note"}
      />
      {/* DS keep (PR 3): the author disc paints the collaborator's own
          awareness colour, which no Avatar paint expresses, at 28px. */}
      {isDraft && author ? (
        <span
          aria-label={`Author ${author.name ?? author.initials}`}
          style={
            authorForeground
              ? {
                  backgroundColor: author.color,
                  color: authorForeground,
                }
              : undefined
          }
          className={cn(
            "pointer-events-none absolute bottom-2 end-2 inline-flex h-7 w-7 items-center justify-center rounded-full text-dense-1 font-semibold shadow-sm",
            authorForeground
              ? "text-pb-on-accent"
              : "border border-pb-border bg-pb-card-elevated text-pb-text",
          )}
        >
          {author.initials}
        </span>
      ) : null}
    </StickyNoteCard>
  );
}

function noteNumber(
  note: Y.Map<unknown>,
  key: string,
  fallback: number,
): number {
  const value = note.get(key);
  return typeof value === "number" && Number.isFinite(value) ? value : fallback;
}

function noteString(
  note: Y.Map<unknown>,
  key: string,
  fallback: string,
): string {
  const value = note.get(key);
  return typeof value === "string" ? value : fallback;
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}
